
import React, { useState, useEffect } from 'react';
import { VoiceId, VOICE_OPTIONS, AccentId, ACCENT_OPTIONS } from '../types';

interface InterfaceOverlayProps {
  isActive: boolean;
  isConnecting: boolean;
  currentSubtitle: string;
  selectedVoice: VoiceId;
  selectedAccent: AccentId;
  hasTranscript: boolean;
  error: string | null;
  
  // New Props for Sharing
  showShareModal?: boolean;
  recordedVideo?: Blob | null;
  onShareVideo?: () => void;
  onSaveVideo?: () => void;
  onDiscardVideo?: () => void;

  onStart: () => void;
  onStop: () => void;
  onVoiceChange: (voice: VoiceId) => void;
  onAccentChange: (accent: AccentId) => void;
  onDownload: () => void;
}

export const InterfaceOverlay: React.FC<InterfaceOverlayProps> = ({
  isActive,
  isConnecting,
  currentSubtitle,
  selectedVoice,
  selectedAccent,
  hasTranscript,
  error,
  showShareModal,
  recordedVideo,
  onStart,
  onStop,
  onShareVideo,
  onSaveVideo,
  onDiscardVideo,
  onVoiceChange,
  onAccentChange,
  onDownload,
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const currentVoiceData = VOICE_OPTIONS.find(v => v.id === selectedVoice);
  const [selectedGender, setSelectedGender] = useState<'male' | 'female'>(currentVoiceData?.gender || 'male');

  useEffect(() => {
    if (currentVoiceData) setSelectedGender(currentVoiceData.gender);
  }, [selectedVoice, currentVoiceData]);

  const handleGenderChange = (gender: 'male' | 'female') => {
    if (gender === selectedGender) return;
    setSelectedGender(gender);
    // Find first available voice of new gender to auto-select
    const newVoice = VOICE_OPTIONS.find(v => v.gender === gender);
    if (newVoice) onVoiceChange(newVoice.id);
  };

  const filteredVoices = VOICE_OPTIONS.filter(v => v.gender === selectedGender);

  return (
    <div className="absolute inset-0 pointer-events-none flex flex-col justify-between z-10">
      
      {/* Header */}
      <div className="p-4 flex justify-between items-start bg-gradient-to-b from-black/60 to-transparent pointer-events-auto">
        {/* Logo Section - Completely removed from DOM when active to prevent double-vision over canvas */}
        {!isActive ? (
          <div className="flex items-center space-x-3 transition-opacity duration-300">
            <div className="w-14 h-14 relative shrink-0">
              <svg viewBox="0 0 100 100" className="w-full h-full filter drop-shadow-md">
                <circle cx="50" cy="50" r="50" fill="#DC2626" />
                <path fill="black" transform="translate(5, 5) scale(0.9)" d="M30 95 C 25 75, 25 55, 40 40 C 45 35, 55 25, 65 32 C 75 36, 88 40, 92 48 C 94 54, 85 58, 80 60 C 75 62, 72 70, 70 80 C 68 88, 65 95, 60 95 Z" />
                <path fill="black" transform="translate(5, 5) scale(0.9)" d="M42 42 C 35 55, 35 70, 45 80 C 50 85, 55 75, 55 65 C 55 55, 50 48, 42 42 Z"/>
              </svg>
            </div>
            <h1 className="text-white font-extrabold text-2xl drop-shadow-md tracking-wider italic">DOGLISH</h1>
          </div>
        ) : (
          <div className="w-14 h-14"></div> // Spacer to keep settings button aligned
        )}

        {/* Settings Button - Always visible */}
        <button onClick={() => setShowSettings(!showSettings)} className="bg-white/10 backdrop-blur-md border border-white/20 p-2 rounded-full text-white hover:bg-white/20 transition">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
          </svg>
        </button>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="absolute top-16 right-4 w-72 bg-gray-900/95 backdrop-blur-lg border border-gray-700 rounded-2xl p-4 shadow-2xl pointer-events-auto transition-all overflow-y-auto max-h-[80vh]">
          <h3 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">Voice Gender</h3>
          <div className="flex bg-black/40 p-1 rounded-xl mb-6 border border-white/5">
            <button onClick={() => handleGenderChange('male')} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${selectedGender === 'male' ? 'bg-blue-600 text-white' : 'text-gray-400'}`}>MALE</button>
            <button onClick={() => handleGenderChange('female')} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${selectedGender === 'female' ? 'bg-pink-600 text-white' : 'text-gray-400'}`}>FEMALE</button>
          </div>

          <h3 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">Persona</h3>
          <div className="space-y-2 mb-6">
            {filteredVoices.map((option) => (
              <button key={option.id} onClick={() => onVoiceChange(option.id)} className={`w-full text-left p-2.5 rounded-xl flex items-center justify-between transition-all ${selectedVoice === option.id ? 'bg-indigo-600 text-white' : 'bg-white/5 text-gray-300'}`}>
                <div><div className="font-semibold text-sm">{option.label}</div><div className="text-[10px] opacity-70">{option.description}</div></div>
                {selectedVoice === option.id && <div className="w-1.5 h-1.5 rounded-full bg-white ml-2"></div>}
              </button>
            ))}
          </div>

          <h3 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-2 pt-4 border-t border-gray-800">Accent</h3>
          <div className="grid grid-cols-2 gap-2 mb-6">
            {ACCENT_OPTIONS.map((option) => (
               <button key={option.id} onClick={() => onAccentChange(option.id)} className={`p-2 rounded-lg text-xs font-medium text-center ${selectedAccent === option.id ? 'bg-emerald-600 text-white' : 'bg-white/5 text-gray-400'}`}>{option.label}</button>
            ))}
          </div>

          <h3 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-2 pt-4 border-t border-gray-800">Data</h3>
          <button onClick={onDownload} disabled={!hasTranscript} className={`w-full p-3 rounded-xl flex items-center justify-center space-x-2 transition-all ${hasTranscript ? 'bg-blue-600 text-white' : 'bg-white/5 text-gray-500'}`}>
            <span className="font-medium text-sm">Save Transcript</span>
          </button>
        </div>
      )}

      {/* Share Video Modal */}
      {showShareModal && recordedVideo && (
        <div className="absolute inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6 pointer-events-auto animate-in fade-in duration-300">
          <div className="bg-gray-900 border border-gray-700 rounded-3xl p-2 w-full max-w-sm shadow-2xl flex flex-col">
            <div className="relative rounded-2xl overflow-hidden bg-black aspect-[9/16] mb-4 shadow-lg">
              <video 
                src={URL.createObjectURL(recordedVideo)} 
                controls 
                autoPlay 
                loop 
                playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded-md shadow">PREVIEW</div>
            </div>
            
            <div className="px-2 pb-2 space-y-3">
              <button 
                onClick={onShareVideo}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl text-white font-bold text-lg shadow-lg shadow-purple-500/30 flex items-center justify-center space-x-2 hover:scale-[1.02] transition-transform"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                <span>Share to Instagram</span>
              </button>

              <button 
                onClick={onSaveVideo}
                className="w-full py-3 bg-blue-600 rounded-xl text-white font-semibold shadow-lg shadow-blue-500/30 flex items-center justify-center space-x-2 hover:bg-blue-500 transition-colors"
              >
                 <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                 <span>Save to Device</span>
              </button>
              
              <button 
                onClick={onDiscardVideo}
                className="w-full py-2 text-gray-400 font-medium text-xs hover:text-white transition-colors"
              >
                Discard Video
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subtitles */}
      <div className={`flex-1 flex items-end justify-center pb-32 px-6 pointer-events-none`}>
        {error ? (
           <div className="bg-red-500/80 backdrop-blur text-white px-6 py-4 rounded-2xl max-w-sm text-center shadow-lg mb-8">{error}</div>
        ) : (
          /* ONLY render HTML subtitles if NOT active. If active, Canvas renders them. */
          !isActive && currentSubtitle && (
            <div className="bg-black/40 backdrop-blur-sm px-6 py-4 rounded-2xl max-w-md text-center shadow-lg mb-8 animate-in slide-in-from-bottom-4 duration-300">
              <p className="text-yellow-300 font-bold text-xl md:text-2xl drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] leading-relaxed tracking-wide">"{currentSubtitle}"</p>
            </div>
          )
        )}
      </div>

      {/* Record Button */}
      <div className="absolute bottom-0 w-full p-8 flex justify-center items-center pointer-events-auto bg-gradient-to-t from-black via-black/50 to-transparent h-48">
        {isActive ? (
          <button onClick={onStop} className="group relative flex items-center justify-center w-20 h-20 bg-transparent rounded-full border-4 border-white shadow-lg hover:scale-105 transition-all">
             <div className="w-8 h-8 bg-red-500 rounded-md"></div>
             <div className="absolute inset-0 rounded-full border-2 border-red-500 opacity-50 recording-pulse"></div>
          </button>
        ) : (
          <button onClick={onStart} disabled={isConnecting} className={`flex items-center justify-center w-20 h-20 rounded-full border-4 border-white transition-all shadow-lg ${isConnecting ? 'bg-gray-600 cursor-wait' : 'bg-transparent hover:scale-110'}`}>
            {isConnecting ? (
               <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            ) : (
              <div className="w-16 h-16 bg-red-600 rounded-full border-2 border-transparent"></div>
            )}
          </button>
        )}
      </div>
      
      <div className="absolute bottom-6 w-full text-center pointer-events-none">
        <p className="text-white/50 text-xs uppercase tracking-widest font-medium">{isActive ? 'Recording...' : isConnecting ? 'Connecting...' : 'Tap to Record'}</p>
      </div>
    </div>
  );
};
